import { addDays, format, startOfDay } from 'date-fns';
import { getPayrollEntries, type PayrollEntry } from './payroll';
import { checkPeriodRunStatus, getPayrollRuns } from './payrollRuns';
import {
  getPastEndedPeriodRanges,
  getCurrentPeriodRange,
  getLastPeriodRange,
  getPreviousPeriodRange,
  getPayrollPeriodDaysPastDue,
  isPayrollPeriodStillOpen,
  type PayrollPeriodSettings,
  type DateRange,
} from './payrollPeriodSettings';

export interface PastDuePayrollPeriod {
  start: Date;
  end: Date;
  totalEarnings: number;
  totalHours: number;
  resourceCount: number;
  unpaidEntryCount: number;
  daysPastDue: number;
  hasBeenRun: boolean;
}

function summarizeUnpaidEntries(entries: Awaited<ReturnType<typeof getPayrollEntries>>) {
  const unpaid = entries.filter((entry) => entry.process_status !== 'PAID' && entry.process_status !== 'VOIDED');
  const resourceIds = new Set(unpaid.map((entry) => entry.resource_id));

  return {
    unpaid,
    totalEarnings: unpaid.reduce((sum, entry) => sum + (entry.earnings || 0), 0),
    totalHours: unpaid.reduce((sum, entry) => sum + (entry.hours || 0), 0),
    resourceCount: resourceIds.size,
    unpaidEntryCount: unpaid.length,
  };
}

async function getPastDuePeriodSummary(
  businessId: string,
  period: DateRange
): Promise<PastDuePayrollPeriod | null> {
  const startStr = format(period.start, 'yyyy-MM-dd');
  const endStr = format(period.end, 'yyyy-MM-dd');
  const entries = await getPayrollEntries(businessId, startStr, endStr);
  const summary = summarizeUnpaidEntries(entries);

  if (summary.unpaidEntryCount === 0) {
    return null;
  }

  // Period is still open on its last day — hours can still accumulate.
  if (isPayrollPeriodStillOpen(period.end)) {
    return null;
  }

  const runStatus = await checkPeriodRunStatus(businessId, period.start, period.end);
  if (runStatus.isVoided) return null;
  const daysPastDue = getPayrollPeriodDaysPastDue(period.end);

  return {
    start: period.start,
    end: period.end,
    totalEarnings: summary.totalEarnings,
    totalHours: summary.totalHours,
    resourceCount: summary.resourceCount,
    unpaidEntryCount: summary.unpaidEntryCount,
    daysPastDue,
    hasBeenRun: runStatus.hasBeenRun,
  };
}

export async function getDueAndOverduePayrollPeriods(
  businessId: string,
  settings: PayrollPeriodSettings,
  maxPastPeriods = 24
): Promise<PastDuePayrollPeriod[]> {
  const pastDue = await getPastDuePayrollPeriods(businessId, settings, maxPastPeriods);

  const currentRange = getCurrentPeriodRange(settings);
  const current = await getPastDuePeriodSummary(businessId, currentRange);

  const periodKey = (period: PastDuePayrollPeriod) =>
    `${format(period.start, 'yyyy-MM-dd')}_${format(period.end, 'yyyy-MM-dd')}`;
  const seen = new Set(pastDue.map(periodKey));

  const all =
    current && !seen.has(periodKey(current)) ? [...pastDue, current] : pastDue;

  return all.sort((a, b) => b.daysPastDue - a.daysPastDue);
}

export async function getPastDuePayrollPeriods(
  businessId: string,
  settings: PayrollPeriodSettings,
  maxPeriods = 24
): Promise<PastDuePayrollPeriod[]> {
  const candidatePeriods = getPastEndedPeriodRanges(settings, maxPeriods);
  const summaries = await Promise.all(
    candidatePeriods.map((period) => getPastDuePeriodSummary(businessId, period))
  );

  return summaries
    .filter((period): period is PastDuePayrollPeriod => period !== null)
    .sort((a, b) => a.daysPastDue - b.daysPastDue);
}

export interface UnrunPayrollPeriod {
  start: Date;
  end: Date;
  totalEarnings: number;
  totalHours: number;
  straightHours: number;
  overtimeHours: number;
  resourceCount: number;
  unpaidEntryCount: number;
  isCurrent: boolean;
  isLast: boolean;
}

function periodKey(period: DateRange): string {
  return `${format(period.start, 'yyyy-MM-dd')}_${format(period.end, 'yyyy-MM-dd')}`;
}

function timeSplit(entry: PayrollEntry): { straightMinutes: number; overtimeMinutes: number } {
  if (entry.entry_type !== 'timeclock') return { straightMinutes: 0, overtimeMinutes: 0 };
  const overtimeMinutes = (entry.overtime_minutes || 0) + (entry.double_time_minutes || 0);
  const totalMinutes = entry.total_minutes || Math.round((entry.hours || 0) * 60);
  const straightMinutes = entry.regular_minutes != null
    ? entry.regular_minutes
    : Math.max(0, totalMinutes - overtimeMinutes);
  return { straightMinutes, overtimeMinutes };
}

function entryFallsInPeriod(entry: PayrollEntry, period: DateRange): boolean {
  const raw = entry.entry_type === 'timeclock'
    ? entry.clock_in
    : entry.entry_type === 'flat_rate' || entry.entry_type === 'commission'
      ? entry.completed_at
      : entry.created_at;
  if (!raw) return false;
  const at = new Date(raw).getTime();
  return at >= period.start.getTime() && at <= period.end.getTime();
}

/** Current period, then each previous one, back to the schedule start (capped). */
function collectSchedulePeriods(settings: PayrollPeriodSettings): DateRange[] {
  const periods: DateRange[] = [];
  const floor = startOfDay(addDays(new Date(), -365 * 3));
  let settingsStart: Date | null = null;
  if (settings.start_date) {
    const [year, month, day] = settings.start_date.split('-').map(Number);
    settingsStart = startOfDay(new Date(year, month - 1, day));
  }

  let cursor = getCurrentPeriodRange(settings);
  for (let i = 0; i < 72; i++) {
    periods.push(cursor);
    if (startOfDay(cursor.start) <= floor) break;

    const previous = getPreviousPeriodRange(settings, cursor);
    if (previous.start.getTime() === cursor.start.getTime()) break;
    if (settingsStart && startOfDay(previous.end) < settingsStart) break;
    cursor = previous;
  }

  return periods;
}

/**
 * Pay periods that still have unpaid hours or pay.
 * A completed run for some people does not hide the period while others are unpaid.
 * A voided period stays off the list. Newest first. Current and last are flags, not separate rows.
 */
export async function getUnrunPayrollPeriods(
  businessId: string,
  settings: PayrollPeriodSettings,
): Promise<UnrunPayrollPeriod[]> {
  const periods = collectSchedulePeriods(settings);
  if (periods.length === 0) return [];

  const current = getCurrentPeriodRange(settings);
  const last = getLastPeriodRange(settings);
  const oldest = periods[periods.length - 1];
  const newest = periods[0];

  const [entries, runs] = await Promise.all([
    getPayrollEntries(
      businessId,
      format(oldest.start, 'yyyy-MM-dd'),
      format(newest.end, 'yyyy-MM-dd'),
      { unpaidOnly: true },
    ),
    getPayrollRuns(businessId),
  ]);

  const voidedKeys = new Set(
    runs
      .filter((run) => run.status === 'voided')
      .map((run) => `${run.period_start_date.slice(0, 10)}_${run.period_end_date.slice(0, 10)}`),
  );

  const unpaid = entries.filter((entry) => {
    if (entry.process_status === 'PAID' || entry.process_status === 'VOIDED') return false;
    if (entry.entry_type === 'timeclock' && !entry.clock_out) return false;
    return true;
  });

  return periods
    .filter((period) => !voidedKeys.has(periodKey(period)))
    .map((period) => {
      const inPeriod = unpaid.filter((entry) => entryFallsInPeriod(entry, period));
      const resourceIds = new Set(inPeriod.map((entry) => entry.resource_id));
      const split = inPeriod.reduce(
        (sum, entry) => {
          const part = timeSplit(entry);
          return {
            straightMinutes: sum.straightMinutes + part.straightMinutes,
            overtimeMinutes: sum.overtimeMinutes + part.overtimeMinutes,
          };
        },
        { straightMinutes: 0, overtimeMinutes: 0 },
      );
      return {
        start: period.start,
        end: period.end,
        totalEarnings: inPeriod.reduce((sum, entry) => sum + (entry.earnings || 0), 0),
        totalHours: inPeriod.reduce((sum, entry) => sum + (entry.hours || 0), 0),
        straightHours: split.straightMinutes / 60,
        overtimeHours: split.overtimeMinutes / 60,
        resourceCount: resourceIds.size,
        unpaidEntryCount: inPeriod.length,
        isCurrent: periodKey(period) === periodKey(current),
        isLast: periodKey(period) === periodKey(last),
      };
    })
    .filter((period) => period.unpaidEntryCount > 0 && (period.totalHours > 0 || period.totalEarnings !== 0));
}
